import React, { useEffect } from 'react'
import useReactRouter from 'use-react-router'
import { Table, Popconfirm, Layout, PageHeader } from 'antd'
import notification from 'mrx-notification'
import { ColumnsType } from 'antd/lib/table'
import useHeightResize from 'utils/useHeightResize'
import { Breadcrumbs } from 'components'
import * as rootStyles from 'assets/layoutStyle'
import styles from './CustomGatewaySettingsList.module.scss'
import { connector, PropsFromRedux } from './container'
import { CustomGatewaySetting } from '../../types'
import CreateCustomGatewaySetting from '../CreateCustomGatewaySetting'

type HeightResizeRule = [number, number, number]

const rules: HeightResizeRule[] = [
  [0, 800, 6],
  [800, 1000, 7],
  [1000, 1200, 9],
  [1200, Infinity, 10]
]

const CustomGatewaySettingsList = (props: PropsFromRedux) => {
  const {
    customGatewaySettingsList,
    isLoading,
    error,
    isNeedToFetchList,
    total,
    currentPage,

    changeCurrentPage,
    changePageSize,
    fetchCustomGatewaySettingsList,

    removeCustomGatewaySetting
  } = props

  const { history } = useReactRouter()

  const handleChangePageSize = (newSize: number) => {
    changePageSize(newSize)
    fetchCustomGatewaySettingsList()
  }

  useHeightResize(handleChangePageSize, rules)

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Custom Gateway settings list page error!',
        description: error
      })
    }
  }, [error])

  useEffect(() => {
    isNeedToFetchList && fetchCustomGatewaySettingsList()
  }, [fetchCustomGatewaySettingsList, isNeedToFetchList])

  const doConfirm = async (customGatewaySettingId: number, e?: React.MouseEvent<HTMLElement>) => {
    e && e.stopPropagation()
    const isRemoved = await removeCustomGatewaySetting(customGatewaySettingId)
    if (isRemoved) {
      notification.success({
        message: 'Successfully',
        description: 'Custom Gateway setting was deleted successfully!'
      })
    }
  }

  const columns: ColumnsType<CustomGatewaySetting> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Client ID',
      render: (record: CustomGatewaySetting) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.clientId}</div>,
      key: 'clientId'
    },
    {
      title: 'Client Secret',
      render: (record: CustomGatewaySetting) => (
        <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.clientSecret}</div>
      ),
      key: 'clientSecret'
    },
    {
      title: 'Retailer API Key',
      render: (record: CustomGatewaySetting) => (
        <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.retailerApiKey}</div>
      ),
      key: 'retailerApiKey'
    },
    {
      title: 'Retailer Company Ref Id',
      dataIndex: 'retailerCompanyRefId',
      key: 'retailerCompanyRefId'
    },
    {
      title: 'Supplier API Key',
      render: (record: CustomGatewaySetting) => (
        <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.supplierApiKey}</div>
      ),
      key: 'supplierApiKey'
    },
    {
      title: 'Supplier Company Ref Id',
      dataIndex: 'supplierCompanyRefId',
      key: 'supplierCompanyRefId'
    },
    {
      title: 'Internal Login',
      dataIndex: 'internalLogin',
      key: 'internalLogin'
    },
    {
      title: 'Internal Password',
      render: (record: CustomGatewaySetting) => (
        <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{record.internalPassword}</div>
      ),
      key: 'internalPassword'
    },
    {
      title: 'Carrier Code',
      dataIndex: 'carrierCode',
      key: 'carrierCode'
    },
    {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      key: 'serviceCode'
    },
    {
      title: 'Shipping Carrier',
      dataIndex: 'shippingCarrier',
      key: 'shippingCarrier'
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (record: CustomGatewaySetting) => (
        <span>
          <Popconfirm
            title='Are you sure delete this setting?'
            onConfirm={(e) => doConfirm(record.id, e)}
            onCancel={(e) => e?.stopPropagation()}
            okText='Yes'
            cancelText='No'
          >
            <a
              href='/#'
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              Remove
            </a>
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <Layout className={styles.root}>
      <Layout.Header style={rootStyles.header}>
        <PageHeader onBack={() => history.goBack()} title='Custom Gateway Settings List' extra={[<CreateCustomGatewaySetting key='1' />]} />
      </Layout.Header>
      <Layout.Content style={rootStyles.contentBreadcrumbs}>
        <Breadcrumbs
          items={[
            { title: 'Main', url: '/' },
            { title: 'Settings', url: '/' },
            { title: 'Plugins', url: '' },
            { title: 'Gustom Gateway Settings', url: '/settings/plugins' }
          ]}
        />
      </Layout.Content>
      <Layout.Content className={styles.contentComponent}>
        <Table
          columns={columns}
          dataSource={customGatewaySettingsList}
          bordered
          rowKey={(record) => record.id}
          loading={isLoading}
          pagination={{
            onChange: changeCurrentPage,
            current: currentPage,
            total
          }}
        />
      </Layout.Content>
    </Layout>
  )
}

export default connector(CustomGatewaySettingsList)
